// ============================================================================
// forumView.js — a szoba témalistája (2026-10-09, a chat fórummá alakítása, 2. fázis).
//
// A chat-képernyő keretén belül jelenik meg (ugyanaz a fejléc), amikor a szoba fórum-módú és nincs
// megnyitott téma. Egy témát megnyitva a hozzászólások a megszokott üzenetnézetben jönnek (chatView.js).
// TISZTA nézet: minden állapot a modellben, minden művelet a kezelőkben (main.js).
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { el } from "./dom.js";
import { renderListaJelzes } from "./listaJelzes.js";
import {
  temaSorrend, temaCim, olvasatlanDb, cimSzerkesztoSorok, utolsoHozzaszolo,
  WORKFLOW_ALLAPOTOK, CIMKE_CSOPORTOK, szuresTemakre, cimkeNev, temaCimkei, allapotSzamok, hasznaltCimkek,
  FORRAS_MEZOK, biztonsagosHivatkozas, pecsetekBol, pecsetNaploBol, pecsetAllapot, hataridoLejart, kapcsolatInfo,
} from "./forumData.js";
import { pickSenderName } from "./nameVariants.js";
import { getRoomDisplayName } from "./roomName.js";


function datum(ms, uiLang, ora = true) {
  if (typeof ms !== "number") return "";
  try {
    return new Date(ms).toLocaleString(localeFor(uiLang), ora
      ? { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }
      : { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch (e) {
    return new Date(ms).toISOString().slice(0, 10);
  }
}

/**
 * Az útmutató-szoba leírása a néző nyelvén (2026-10-19): a felület nyelve, majd angol, magyar, japán, végül bármelyik megadott. Üres szöveg, ha nincs.
 * @param {object|null} szoba a szoba (guideText: { hu, en, ja })
 */
export function utmutatoSzoveg(szoba, uiLang) {
  const sz = szoba && szoba.guideText && typeof szoba.guideText === "object" ? szoba.guideText : {};
  for (const nyelv of [uiLang, "en", "hu", "ja"]) if (typeof sz[nyelv] === "string" && sz[nyelv].trim() !== "") return sz[nyelv].trim();
  const barmi = Object.values(sz).find((v) => typeof v === "string" && v.trim() !== "");
  return barmi ? barmi.trim() : "";
}

/** A megnyitott szoba látható alszobái (a tag szobái közül; nem archivált). */
function lathatoAlszobak(model) {
  const lista = (model.rooms || []).filter((r) => r && r.parentRoomId === model.currentRoomId && r.roomId !== model.currentRoomId && !r.archived);
  // az admin által beállított sorrend (v125); a számozatlanok a számozottak után, eredeti sorrendben
  const hely = new Map(lista.map((r, i) => [r.roomId, i]));
  return lista.slice().sort((a, b) => {
    const x = Number.isFinite(a.sortOrder) ? a.sortOrder : Infinity; const y = Number.isFinite(b.sortOrder) ? b.sortOrder : Infinity;
    return x === y ? hely.get(a.roomId) - hely.get(b.roomId) : x - y;
  });
}

const ALSZOBA_FA_MAX_MELYSEG = 5;

/**
 * Az alszobák FÁJA (v148, az útmutató-képernyőhöz): a közvetlen gyerekek, mindegyik alatt a saját látható gyerekei (beljebb), mélységi
 * bejárással; minden szinten az admin sorrendje (lathatoAlszobak). Ciklus és túl mély lánc ellen védett (legfeljebb 5 szint).
 * @returns {Array<{r: object, melyseg: number}>}
 */
function alszobaFa(model) {
  const ki = [];
  const lattuk = new Set([model.currentRoomId]);
  const be = (szuloId, melyseg) => {
    if (melyseg > ALSZOBA_FA_MAX_MELYSEG) return;
    for (const r of lathatoAlszobak({ ...model, currentRoomId: szuloId })) {
      if (lattuk.has(r.roomId)) continue;
      lattuk.add(r.roomId);
      ki.push({ r, melyseg: melyseg - 1 });
      be(r.roomId, melyseg + 1);
    }
  };
  be(model.currentRoomId, 1);
  return ki;
}

/** Hány alszobát lát a tag a megnyitott szobában (az útmutató-szoba "Válassz az alábbi N szoba közül" sorához); melyebb: az unokák is. */
export function alszobakSzama(model, melyebb = false) {
  if (!model.currentRoomId) return 0;
  return melyebb ? alszobaFa(model).length : lathatoAlszobak(model).length;
}

// Az útmutató-szoba "Korábbi beszélgetések" részének nyitott állapota (újrarajzoláskor sem csukódik vissza).
let guideRegiNyitva = false;

/**
 * A szoba ALSZOBÁI (2026-10-18, AI-titkári audit: "a szülőszobából nem látszik, merre lehet továbbmenni"): a megnyitott szoba közvetlen
 * gyermekei, a tag saját szobái közül. A lista a szobalistából jön, amit a Firestore a tag jogosultsága szerint ad (a szabály dönt,
 * nem a képernyő): olyan alszoba, amelyhez nincs hozzáférése, sem névvel, sem darabszámmal nem jelenik meg.
 * @param {object} model { uiLang, contentLang, rooms, currentRoomId, unreadRoomIds }
 * @param {object} handlers onOpenSubRoom(roomId)
 * @param {boolean} kompakt egysoros sáv (a nem fórum-szoba fejléce alatt) a teljes szakasz helyett
 * @param {boolean} melyebb az útmutató-szobában: a mélyebb szintek (unokák, ...) is, beljebb húzva (v148)
 * @returns {HTMLElement|null} null, ha nincs alszoba
 */
export function renderSubRooms(model, handlers, kompakt = false, melyebb = false) {
  const { uiLang, contentLang, currentRoomId } = model;
  // SORREND (v146, a titkár #77 megfigyelése + élő hiba): az alszobák az ADMIN által beállított sorrendben állnak (lathatoAlszobak: sortOrder, a számozatlanok
  // utána), UGYANÚGY, mint a szobaváltóban - korábban itt a név szerinti ábécésorrend felülírta, és az útmutató képernyő eltért a szobaváltótól.
  const gyerekek = (melyebb && !kompakt ? alszobaFa(model) : lathatoAlszobak(model).map((r) => ({ r, melyseg: 0 })))
    .map(({ r, melyseg }) => ({ r, melyseg, nev: getRoomDisplayName(r, contentLang, uiLang) }));
  if (!currentRoomId || gyerekek.length === 0) return null;
  const olvasatlan = new Set(model.unreadRoomIds || []);
  const doboz = el("section", { class: "subrooms" + (kompakt ? " compact" : ""), "aria-label": t(uiLang, "forum.subRooms") });
  doboz.appendChild(el(kompakt ? "span" : "h3", { class: "subrooms-title", text: t(uiLang, "forum.subRooms") }));
  const fa = gyerekek.some((g) => g.melyseg > 0);
  const lista = el("div", { class: "subrooms-list" + (fa ? " tree" : "") });
  for (const { r, nev, melyseg } of gyerekek) {
    // v185 (a Tomita-eset): a teljes szakaszban az alszoba nagy, kártyaszerű bejárat (az egysoros sávban a régi, kicsi gomb marad).
    lista.appendChild(el("button", {
      type: "button", class: "subroom-btn" + (kompakt ? "" : " subroom-card"), "data-depth": String(melyseg), onclick: () => handlers.onOpenSubRoom(r.roomId),
    }, [
      el("span", { class: kompakt ? "" : "subroom-card-name", text: "↳ " + nev }),
      olvasatlan.has(r.roomId) ? el("span", { class: "unread-dot", "aria-label": t(uiLang, "chat.unreadElsewhere"), title: t(uiLang, "chat.unreadElsewhere") }) : null,
    ]));
  }
  doboz.appendChild(lista);
  return doboz;
}

/**
 * A szoba útvonala (2026-10-18, a titkár javaslata: "ősszoba > alszoba", hogy látszódjon a mostani hely és a visszaút): az ősszobák
 * gombok (kattintásra megnyílnak), a mostani szoba szöveg. Csak azok az ősök jelennek meg, amelyeket a tag lát (a model.rooms a tag szobái);
 * ha nincs látható ős, nincs sáv. A ciklusos / túl mély láncot megszakítja (legfeljebb 6 szint).
 * @returns {HTMLElement|null}
 */
export function renderSzobaUtvonal(model, handlers) {
  const { uiLang, contentLang, currentRoomId } = model;
  const szobak = model.rooms || [];
  const aktualis = szobak.find((r) => r && r.roomId === currentRoomId);
  if (!aktualis) return null;
  const lanc = [];
  const lattuk = new Set([aktualis.roomId]);
  let r = aktualis;
  while (r && r.parentRoomId && lanc.length < 6) {
    const szulo = szobak.find((x) => x && x.roomId === r.parentRoomId && !x.archived);
    if (!szulo || lattuk.has(szulo.roomId)) break;
    lattuk.add(szulo.roomId);
    lanc.unshift(szulo);
    r = szulo;
  }
  if (lanc.length === 0) return null;
  const sav = el("nav", { class: "room-path", "aria-label": t(uiLang, "forum.pathLabel") });
  for (const szulo of lanc) {
    sav.appendChild(el("button", {
      type: "button", class: "text-btn room-path-link", text: getRoomDisplayName(szulo, contentLang, uiLang),
      onclick: () => handlers.onOpenSubRoom && handlers.onOpenSubRoom(szulo.roomId),
    }));
    sav.appendChild(el("span", { class: "room-path-sep", text: " › " }));
  }
  sav.appendChild(el("span", { class: "room-path-here", "aria-current": "page", text: getRoomDisplayName(aktualis, contentLang, uiLang) }));
  return sav;
}

/**
 * A megnyitott téma útvonala (2026-10-19, a titkár #35): "Szobák › Ősszoba › Szoba › Téma" - a "Szobák" a szobalistát nyitja (szöveggel,
 * nem csak nyílikonnal), az ősszobák és a mostani szoba gombok (a mostani szoba a témalistára vezet), a téma címe szöveg. A hosszú nevek
 * tördelődnek (nem vágódnak le), hogy gépelés közben is látszódjon, hol vagyunk.
 * @returns {HTMLElement|null} null, ha nincs szoba
 */
export function renderTemaUtvonal(model, handlers) {
  const { uiLang, contentLang, currentRoomId, aktTema } = model;
  const szobak = model.rooms || [];
  const aktualis = szobak.find((r) => r && r.roomId === currentRoomId);
  if (!aktualis) return null;
  const lanc = [];
  const lattuk = new Set([aktualis.roomId]);
  let r = aktualis;
  while (r && r.parentRoomId && lanc.length < 6) {
    const szulo = szobak.find((x) => x && x.roomId === r.parentRoomId && !x.archived);
    if (!szulo || lattuk.has(szulo.roomId)) break;
    lattuk.add(szulo.roomId);
    lanc.unshift(szulo);
    r = szulo;
  }
  const sav = el("nav", { class: "room-path topic-path", "aria-label": t(uiLang, "forum.pathLabel") });
  const elvalaszto = () => sav.appendChild(el("span", { class: "room-path-sep", text: " › " }));
  const gomb = (szoveg, kezelo) => sav.appendChild(el("button", { type: "button", class: "text-btn room-path-link", text: szoveg, onclick: kezelo }));
  if (handlers && handlers.onOpenRooms) { gomb(t(uiLang, "chat.rooms"), handlers.onOpenRooms); elvalaszto(); }
  for (const szulo of lanc) {
    gomb(getRoomDisplayName(szulo, contentLang, uiLang), () => handlers.onOpenSubRoom && handlers.onOpenSubRoom(szulo.roomId));
    elvalaszto();
  }
  gomb(getRoomDisplayName(aktualis, contentLang, uiLang), () => handlers.onBackToTopics && handlers.onBackToTopics());
  if (aktTema) {
    elvalaszto();
    const elozmeny = aktTema.kind === "history";
    sav.appendChild(el("span", { class: "room-path-here", "aria-current": "page", text: elozmeny ? t(contentLang, "forum.historyTitle") : temaCim(aktTema, contentLang) }));
  }
  return sav;
}

/**
 * A státusz megjegyzése: vékony sor a téma sávja alatt (2026-10-18), ha van - mindenkinek látszik ("miért van visszaküldve?"). Kattintásra az előzmény nyílik.
 */
export function renderWorkflowNote(tema, uiLang, handlers) {
  if (!tema || !tema.workflow || !(tema.workflowNote || tema.workflowAssignee || tema.workflowDue)) return null;
  const sor = el("div", { class: "workflow-note" });
  if (tema.workflow) {
    sor.appendChild(allapotJelveny(uiLang, tema.workflow));
    sor.appendChild(document.createTextNode(" "));
  }
  const felelosHatarido = felelosHataridoJelzes(tema, uiLang);
  if (felelosHatarido) {
    sor.appendChild(felelosHatarido);
    sor.appendChild(document.createTextNode(" "));
  }
  if (tema.workflowNote) sor.appendChild(el("span", { class: "workflow-note-text", text: tema.workflowNote }));
  if (handlers && handlers.onOpenWorkflowPanel) {
    sor.appendChild(el("button", { type: "button", class: "text-btn workflow-history-btn", text: "🕘", title: t(uiLang, "forum.workflowHistory"), "aria-label": t(uiLang, "forum.workflowHistory"), onclick: handlers.onOpenWorkflowPanel }));
  }
  return sor;
}

/**
 * A státusz-előzmény lapja (2026-10-18): a jelenlegi státusz, a változások naplója (ki, mikor, melyikről melyikre, megjegyzés), és - akinek
 * van joga - a státusz módosítása megjegyzéssel (pl. a visszaküldés okával). A vázlat a modellben él (wfPanel), a gépelés nem rajzol újra.
 * @param {object} model { uiLang, aktTema, temaKezelhet, wfPanel: {workflow, note, busy, error} }
 */
export function renderWorkflowPanel(model, handlers) {
  const { uiLang, aktTema } = model;
  const panel = model.wfPanel || { workflow: "", note: "" };
  const box = el("section", { class: "panel-section forum-workflow-panel" });
  box.appendChild(el("h3", { text: t(uiLang, "forum.workflowHistory") }));
  const jelen = el("div", { class: "workflow-current" }, [el("strong", { text: t(uiLang, "forum.workflowCurrent") + ": " })]);
  jelen.appendChild(aktTema.workflow ? allapotJelveny(uiLang, aktTema.workflow) : el("span", { text: t(uiLang, "forum.wf_none") }));
  box.appendChild(jelen);

  if (model.temaKezelhet) {
    const sel = el("select", { class: "panel-input", "aria-label": t(uiLang, "forum.workflowLabel") });
    sel.appendChild(el("option", { value: "", text: t(uiLang, "forum.wf_none") }));
    for (const a of WORKFLOW_ALLAPOTOK) {
      const o = el("option", { value: a, text: t(uiLang, `forum.wf_${a}`) });
      if (panel.workflow === a) o.selected = true;
      sel.appendChild(o);
    }
    sel.addEventListener("change", () => handlers.onWorkflowPanelChange("workflow", sel.value));
    box.appendChild(sel);
    box.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "forum.assigneeLabel") }));
    const felelos = el("input", { class: "panel-input", type: "text", maxlength: "60" });
    felelos.value = panel.assignee || "";
    felelos.addEventListener("input", () => handlers.onWorkflowPanelChange("assignee", felelos.value));
    box.appendChild(felelos);
    box.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "forum.dueLabel") }));
    const hatarido = el("input", { class: "panel-input", type: "date", min: "2000-01-01", max: "2100-12-31" });
    hatarido.value = panel.due || "";
    hatarido.addEventListener("input", () => handlers.onWorkflowPanelChange("due", hatarido.value));
    box.appendChild(hatarido);
    box.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "forum.workflowNoteLabel") }));
    const megj = el("textarea", { class: "panel-input", rows: "2", maxlength: "300" });
    megj.value = panel.note || "";
    megj.addEventListener("input", () => handlers.onWorkflowPanelChange("note", megj.value));
    box.appendChild(megj);
    if (panel.error) box.appendChild(el("div", { class: "panel-status err", role: "alert", text: panel.error }));
    const gombok = el("div", { class: "panel-row" });
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: panel.busy === true, onclick: handlers.onWorkflowPanelClose }));
    gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: panel.busy ? "…" : t(uiLang, "glossary.save"), disabled: panel.busy === true, onclick: handlers.onWorkflowPanelSave }));
    box.appendChild(gombok);
  } else {
    box.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "admin.close"), onclick: handlers.onWorkflowPanelClose }));
  }

  const naplo = (aktTema && aktTema.workflowLog) || [];
  const lista = el("div", { class: "workflow-log" });
  if (naplo.length === 0) lista.appendChild(el("p", { class: "hint", text: t(uiLang, "forum.workflowNoHistory") }));
  for (const b of naplo) {
    const ki = b.by === "deleted" ? t(uiLang, "msg.deletedSender") : b.byName || "?";
    const nev = (a) => (a ? t(uiLang, `forum.wf_${a}`) : t(uiLang, "forum.wf_none"));
    const valtozas = b.from === b.to ? `${nev(b.to)} ✎` : `${nev(b.from)} → ${nev(b.to)}`;
    const sor = el("div", { class: "workflow-log-row" }, [el("div", { class: "workflow-log-head", text: `${ki} · ${datum(b.at, uiLang)}: ${valtozas}` })]);
    if (b.note) sor.appendChild(el("div", { class: "workflow-log-note", text: b.note }));
    lista.appendChild(sor);
  }
  box.appendChild(lista);
  return box;
}

/**
 * A téma forrásmegjelölése: rövid kártya a téma felett (2026-10-18). Üres megjelölésnél nincs kártya.
 * A hivatkozás csak https lehet, új lapon nyílik (noopener).
 */
export function renderSourceCard(tema, uiLang) {
  const f = (tema && tema.source) || {};
  if (FORRAS_MEZOK.every((k) => !f[k])) return null;
  const kartya = el("section", { class: "source-card", "aria-label": t(uiLang, "forum.source") });
  const sor = el("div", { class: "source-line" }, [el("strong", { text: t(uiLang, "forum.source") + ": " })]);
  const reszek = [];
  if (f.work) reszek.push(el("span", { class: "source-work", text: f.work }));
  if (f.year) reszek.push(el("span", { text: f.year }));
  if (f.pages) reszek.push(el("span", { text: "p. " + f.pages }));
  if (f.holder) reszek.push(el("span", { text: f.holder }));
  reszek.forEach((r, i) => {
    if (i > 0) sor.appendChild(document.createTextNode(" · "));
    sor.appendChild(r);
  });
  const hiv = biztonsagosHivatkozas(f.link);
  if (hiv) {
    let nev = hiv;
    try { nev = new URL(hiv).hostname; } catch (e) { /* marad a teljes cím */ }
    sor.appendChild(document.createTextNode(" "));
    sor.appendChild(el("a", { href: hiv, target: "_blank", rel: "noopener noreferrer", class: "source-link", text: "↗ " + nev }));
  }
  kartya.appendChild(sor);
  if (f.terms) kartya.appendChild(el("div", { class: "source-terms", text: f.terms }));
  return kartya;
}

/**
 * A forrásmegjelölés szerkesztője (a téma nyitója / admin / témafelelős). A vázlat a modellben él (sourceDraft), a gépelés nem rajzol újra.
 * @param {object} model { uiLang, sourceDraft, sourceBusy, sourceError }
 */
export function renderSourceEditor(model, handlers) {
  const { uiLang } = model;
  const box = el("section", { class: "panel-section forum-source-editor" });
  box.appendChild(el("h3", { text: t(uiLang, "forum.sourceEdit") }));
  box.appendChild(el("p", { class: "hint", text: t(uiLang, "forum.sourceHint") }));
  const cimkek = { work: "forum.sourceWork", year: "forum.sourceYear", pages: "forum.sourcePages", holder: "forum.sourceHolder", terms: "forum.sourceTerms", link: "forum.sourceLink" };
  const hossz = { work: "200", year: "40", pages: "80", holder: "200", terms: "300", link: "500" };
  const vazlat = model.sourceDraft || {};
  for (const k of FORRAS_MEZOK) {
    box.appendChild(el("label", { class: "panel-label small", text: t(uiLang, cimkek[k]) }));
    const mezo = el("input", { type: k === "link" ? "url" : "text", class: "panel-input", maxlength: hossz[k], inputmode: k === "link" ? "url" : null });
    mezo.value = vazlat[k] || "";
    mezo.addEventListener("input", () => handlers.onSourceChange(k, mezo.value));
    box.appendChild(mezo);
  }
  if (model.sourceError) box.appendChild(el("div", { class: "panel-status err", role: "alert", text: model.sourceError }));
  const gombok = el("div", { class: "panel-row" });
  gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: model.sourceBusy, onclick: handlers.onSourceCancel }));
  gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: model.sourceBusy ? "…" : t(uiLang, "glossary.save"), disabled: model.sourceBusy, onclick: handlers.onSourceSave }));
  box.appendChild(gombok);
  return box;
}

/** Felelős és határidő kis jelzése ("👤 név · 📅 dátum"; lejárt határidőnél figyelmeztető szín és felirat). null, ha egyik sincs. */
function felelosHataridoJelzes(tema, uiLang) {
  if (!tema || !(tema.workflowAssignee || tema.workflowDue)) return null;
  const lejart = hataridoLejart(tema.workflowDue, tema.workflow);
  const reszek = [];
  if (tema.workflowAssignee) reszek.push("👤 " + tema.workflowAssignee);
  if (tema.workflowDue) reszek.push("📅 " + tema.workflowDue + (lejart ? " · " + t(uiLang, "forum.overdue") : ""));
  return el("span", { class: "wf-owner" + (lejart ? " overdue" : ""), text: reszek.join(" · ") });
}

/** A munkafolyamat-státusz jelvénye (szín + felirat; a szín önmagában nem hordoz jelentést, a felirat is ott van). */
function allapotJelveny(uiLang, workflow) {
  return el("span", { class: `wf-badge wf-${workflow}`, text: t(uiLang, `forum.wf_${workflow}`) });
}

/**
 * A témalista szűrősora (2026-10-16): státusz-választó + a katalógus címkéi csoportonként, kapcsolható gombokkal.
 * Csak akkor jelenik meg, ha van mit szűrni (katalógus vagy valamelyik témán státusz).
 */
function szuroSor(model, handlers) {
  const { uiLang, contentLang } = model;
  const szuro = model.forumSzuro || { workflow: "", tags: [] };
  const vanAllapot = (model.topics || []).some((x) => x.workflow);
  // A szűrő csak azokat a címkéket kínálja, amelyeket valamelyik TÉMA ténylegesen visel (a hozzászólásokra szánt, pl. állapotjelző
  // címkék nem zsúfolják a témalistát) - a most bekapcsoltak mindig látszanak, hogy kikapcsolhatók legyenek. 2026-10-16.
  const hasznalt = new Set(hasznaltCimkek(model.topics, model.tagCatalog || []).map((c) => c.id));
  const katalogus = (model.tagCatalog || []).filter((c) => hasznalt.has(c.id) || szuro.tags.includes(c.id));
  if (katalogus.length === 0 && !vanAllapot && !szuro.workflow) return null;
  const doboz = el("section", { class: "forum-filter" });
  const szamok = allapotSzamok(model.topics);
  const sel = el("select", { class: "panel-input forum-filter-status", "aria-label": t(uiLang, "forum.filterStatus") });
  sel.appendChild(el("option", { value: "", text: t(uiLang, "forum.filterAllStatus") }));
  for (const a of WORKFLOW_ALLAPOTOK) {
    const o = el("option", { value: a, text: `${t(uiLang, `forum.wf_${a}`)} (${szamok[a]})` });
    if (szuro.workflow === a) o.selected = true;
    sel.appendChild(o);
  }
  sel.addEventListener("change", () => handlers.onFilterWorkflow(sel.value));
  doboz.appendChild(sel);
  for (const csoport of CIMKE_CSOPORTOK) {
    const tagek = katalogus.filter((c) => c.group === csoport);
    if (tagek.length === 0) continue;
    const sor = el("div", { class: "tag-row" }, [el("span", { class: "tag-row-label", text: t(uiLang, `forum.tagGroup_${csoport}`) })]);
    for (const c of tagek) {
      const be = szuro.tags.includes(c.id);
      sor.appendChild(el("button", {
        type: "button", class: `tag-chip tag-${c.group}` + (be ? " active" : ""), text: cimkeNev(c, contentLang),
        "aria-pressed": be ? "true" : "false", onclick: () => handlers.onFilterTag(c.id),
      }));
    }
    doboz.appendChild(sor);
  }
  if (szuro.workflow || szuro.tags.length > 0) {
    doboz.appendChild(el("button", { type: "button", class: "text-btn", text: t(uiLang, "forum.filterClear"), onclick: handlers.onFilterClear }));
  }
  return doboz;
}

/**
 * A témához rendelhető címkék kiválasztója (a téma nyitója / admin / témafelelős): csoportonként jelölőnégyzetek a
 * katalógusból. A jelölések a vázlatban (model.tagsDraft) élnek, mentés a kezelőben.
 * @param {object} model { uiLang, contentLang, tagCatalog, tagsDraft, tagsBusy, tagsError }
 */
export function renderTagsEditor(model, handlers) {
  const { uiLang, contentLang } = model;
  const box = el("section", { class: "panel-section forum-tags-editor" });
  box.appendChild(el("h3", { text: t(uiLang, "forum.tagsEdit") }));
  const katalogus = model.tagCatalog || [];
  if (katalogus.length === 0) box.appendChild(el("p", { class: "hint", text: t(uiLang, "forum.tagsEmpty") }));
  // Az admin a témából is eléri a katalógust (új címke felvétele); mentés / mégse után ide tér vissza.
  if (model.admin) {
    box.appendChild(el("button", { type: "button", class: "neutral-btn compact", text: "🗂️ " + t(uiLang, "forum.catalogEdit"), onclick: handlers.onCatalogOpen }));
  }
  const kijelolt = model.tagsDraft || [];
  for (const csoport of CIMKE_CSOPORTOK) {
    const tagek = katalogus.filter((c) => c.group === csoport);
    if (tagek.length === 0) continue;
    box.appendChild(el("h4", { text: t(uiLang, `forum.tagGroup_${csoport}`) }));
    for (const c of tagek) {
      const jelolo = el("input", { type: "checkbox", value: c.id });
      jelolo.checked = kijelolt.includes(c.id);
      jelolo.addEventListener("change", () => handlers.onTagsToggle(c.id, jelolo.checked));
      box.appendChild(el("label", { class: "tag-check" }, [jelolo, el("span", { class: `tag-chip small tag-${c.group}`, text: cimkeNev(c, contentLang) })]));
    }
  }
  if (model.tagsError) box.appendChild(el("div", { class: "panel-status err", text: model.tagsError }));
  const gombok = el("div", { class: "panel-row" });
  gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: model.tagsBusy, onclick: handlers.onTagsCancel }));
  gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: model.tagsBusy ? "…" : t(uiLang, "glossary.save"), disabled: model.tagsBusy, onclick: handlers.onTagsSave }));
  box.appendChild(gombok);
  return box;
}

/**
 * A szoba címkekatalógusának szerkesztője (csak admin). Csoportonként egy-egy lista: név + AI-fordítás gomb + törlés;
 * a nyelvenkénti nevek lenyitva szerkeszthetők (lustán épülnek fel: akár 300 címke x 15 nyelv mezője nem terheli a lapot).
 * A gépelés nem rajzol újra (a kezelő a vázlatot frissíti); hozzáadás / törlés / fordítás újrarajzol.
 * @param {object} model { uiLang, katalogusDraft, katalogusBusy, katalogusError, katalogusStatus, nyelvNevek }
 */
export function renderCatalogEditor(model, handlers) {
  const { uiLang } = model;
  const box = el("section", { class: "panel-section forum-catalog-editor" });
  box.appendChild(el("h3", { text: t(uiLang, "forum.catalogEdit") }));
  box.appendChild(el("p", { class: "hint", text: t(uiLang, "forum.catalogHint") }));
  // A visszajelzés (hiba / folyamatban / kész) a szerkesztő TETEJÉN, tapadósan áll: hosszú listánál, főleg telefonon az alul álló üzenet
  // kívül esett a képernyőn, ezért a fordítás / mentés hibája "nem történt semmi"-nek látszott (2026-10-16, iPhone-os jelzés).
  if (model.katalogusError || model.katalogusStatus) {
    box.appendChild(el("div", {
      class: "catalog-message panel-status " + (model.katalogusError ? "err" : "ok"), role: model.katalogusError ? "alert" : "status",
      text: model.katalogusError || model.katalogusStatus,
    }));
  }
  const vazlat = model.katalogusDraft || [];
  for (const csoport of CIMKE_CSOPORTOK) {
    box.appendChild(el("h4", { text: t(uiLang, `forum.tagGroup_${csoport}`) }));
    vazlat.forEach((c, i) => {
      if (c.group !== csoport) return;
      const nev = el("input", { type: "text", class: "panel-input", maxlength: "60", "aria-label": t(uiLang, "forum.catalogName") });
      nev.value = c.name || "";
      nev.addEventListener("input", () => handlers.onCatalogName(i, nev.value));
      // A címke áthelyezhető másik csoportba (pl. egy állapotjelző, ami tévedésből a "Korszak" alá került).
      const csoportValaszto = el("select", { class: "panel-input catalog-group", "aria-label": t(uiLang, "forum.catalogGroup"), title: t(uiLang, "forum.catalogGroup") });
      for (const g of CIMKE_CSOPORTOK) {
        const o = el("option", { value: g, text: t(uiLang, `forum.tagGroup_${g}`) });
        if (g === c.group) o.selected = true;
        csoportValaszto.appendChild(o);
      }
      csoportValaszto.addEventListener("change", () => handlers.onCatalogGroup(i, csoportValaszto.value));
      const sor = el("div", { class: "catalog-row" }, [
        nev,
        csoportValaszto,
        el("button", { type: "button", class: "text-btn", text: "🌐", title: t(uiLang, "forum.catalogSuggest"), "aria-label": t(uiLang, "forum.catalogSuggest"), disabled: model.katalogusBusy, onclick: () => handlers.onCatalogSuggest(i) }),
        el("button", { type: "button", class: "text-btn", text: "✕", title: t(uiLang, "forum.catalogRemove"), "aria-label": t(uiLang, "forum.catalogRemove"), onclick: () => handlers.onCatalogRemove(i) }),
      ]);
      box.appendChild(sor);
      const nyelvek = el("details", { class: "catalog-langs" }, [el("summary", { text: t(uiLang, "forum.catalogLangs", { n: Object.keys(c.names || {}).length }) })]);
      nyelvek.addEventListener("toggle", () => {
        if (!nyelvek.open || nyelvek.dataset.kesz === "1") return;
        nyelvek.dataset.kesz = "1";
        for (const [kod, nyelvNev] of Object.entries(model.nyelvNevek || {})) {
          nyelvek.appendChild(el("label", { class: "panel-label small", text: `${nyelvNev} (${kod})` }));
          const mezo = el("input", { type: "text", class: "panel-input", maxlength: "80" });
          mezo.value = (c.names && c.names[kod]) || "";
          mezo.addEventListener("input", () => handlers.onCatalogLangName(i, kod, mezo.value));
          nyelvek.appendChild(mezo);
        }
      });
      box.appendChild(nyelvek);
    });
    box.appendChild(el("button", { type: "button", class: "neutral-btn compact", text: "+ " + t(uiLang, "forum.catalogAdd"), onclick: () => handlers.onCatalogAdd(csoport) }));
  }
  const gombok = el("div", { class: "panel-row" });
  gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: model.katalogusBusy, onclick: handlers.onCatalogCancel }));
  gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: model.katalogusBusy ? "…" : t(uiLang, "glossary.save"), disabled: model.katalogusBusy, onclick: handlers.onCatalogSave }));
  box.appendChild(gombok);
  return box;
}

/**
 * Kapcsolatok a hozzászóláson (2026-10-18): "Alapja: #N" (módosult-e azóta), "Ennek az utódja: #N", "Újabb változat: #M", "Erre épül: #K". Az azonosítók
 * csak ugyanazon téma hozzászólásaira mutatnak; a névre / számra kattintva oda ugrik. A szerkesztő (postLinking) két választó az érintett téma többi hozzászólásából.
 * @param {object} es { uzenetek, postLinking }
 * @returns {HTMLElement|null}
 */
export function renderPostLinks(msg, es, uiLang, handlers) {
  if (!msg || !msg.messageId || !es) return null;
  const k = kapcsolatInfo(msg, es.uzenetek);
  const vazlat = es.postLinking && es.postLinking.messageId === msg.messageId ? es.postLinking : null;
  if (!k.basedOn && !k.supersedes && k.supersededBy.length === 0 && k.dependents.length === 0 && !vazlat) return null;
  const doboz = el("div", { class: "msg-links" });
  const hiv = (x) => el("button", {
    type: "button", class: "text-btn msg-link-ref", text: x.num ? `#${x.num}` : "↗",
    title: x.name || null, onclick: () => handlers && handlers.onJumpToMessage && handlers.onJumpToMessage(x.id),
  });
  const sor = (kulcs, lista, figyelmeztet) => {
    const s = el("div", { class: "msg-link-row" + (figyelmeztet ? " stale" : "") });
    s.appendChild(el("span", { text: (figyelmeztet ? "⚠ " : "") + t(uiLang, kulcs) + ": " }));
    lista.forEach((x, i) => {
      if (i > 0) s.appendChild(document.createTextNode(", "));
      s.appendChild(hiv(x));
      if (x.stale && !figyelmeztet) s.appendChild(el("span", { class: "msg-link-stale", text: " ⚠" }));
    });
    if (figyelmeztet) s.appendChild(el("span", { text: " — " + t(uiLang, "message.linkBaseChanged") }));
    return s;
  };
  if (k.basedOn) doboz.appendChild(sor("message.linkBasedOn", [k.basedOn], k.basedOn.stale));
  if (k.supersedes) doboz.appendChild(sor("message.linkSupersedes", [k.supersedes], false));
  if (k.supersededBy.length > 0) doboz.appendChild(sor("message.linkSupersededBy", k.supersededBy, false));
  if (k.dependents.length > 0) doboz.appendChild(sor("message.linkDependents", k.dependents, false));
  if (vazlat) {
    const lehetosegek = (es.uzenetek || []).filter((x) => x && x.messageId && x.messageId !== msg.messageId && x.deleted !== true && !x.mergedInto);
    const valaszto = (mezo, kulcs) => {
      const cim = el("label", { class: "panel-label small", text: t(uiLang, kulcs) });
      const sel = el("select", { class: "panel-input" });
      sel.appendChild(el("option", { value: "", text: t(uiLang, "message.linkNone") }));
      for (const x of lehetosegek) {
        const szoveg = typeof x.text === "string" ? x.text.replace(/\s+/g, " ").trim().slice(0, 40) : "";
        const o = el("option", { value: x.messageId, text: `${Number.isInteger(x.postNumber) ? "#" + x.postNumber : "↗"} ${typeof x.senderName === "string" ? x.senderName : ""} · ${szoveg}` });
        if (vazlat[mezo] === x.messageId) o.selected = true;
        sel.appendChild(o);
      }
      sel.addEventListener("change", () => handlers.onPostLinksChange(mezo, sel.value));
      return [cim, sel];
    };
    const szerk = el("div", { class: "post-link-editor" });
    for (const n of valaszto("basedOn", "message.linkBasedOn")) szerk.appendChild(n);
    for (const n of valaszto("supersedes", "message.linkSupersedes")) szerk.appendChild(n);
    if (vazlat.error) szerk.appendChild(el("div", { class: "panel-status err", text: vazlat.error }));
    const gombok = el("div", { class: "panel-row" });
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: vazlat.busy === true, onclick: handlers.onPostLinksCancel }));
    gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: vazlat.busy ? "…" : t(uiLang, "glossary.save"), disabled: vazlat.busy === true, onclick: handlers.onPostLinksSave }));
    szerk.appendChild(gombok);
    doboz.appendChild(szerk);
  }
  return doboz;
}

/**
 * Ellenőrzési pecsétek egy hozzászólás MEGJELENÍTETT nyelvű szövegén (2026-10-18): ki, mikor ellenőrizte; ha a szöveg azóta módosult (szerkesztés,
 * újrafordítás), a pecsét "elavult" jelzést kap, hogy az olvasó ne higgye az új szöveget is ellenőrzöttnek. A gomb: a szöveg ellenőrzése.
 * @param {object} msg a hozzászólás
 * @param {string|null} nyelv a megjelenített szöveg nyelve (forrásnyelv, vagy a néző nyelve fordításnál)
 * @param {string} szoveg a megjelenített szöveg
 * @param {object} es { forumTema, isAdmin, isStaff }
 * @returns {HTMLElement|null}
 */
export function renderPecsetek(msg, nyelv, szoveg, uiLang, myUid, es, handlers) {
  if (!msg || !msg.messageId || !nyelv) return null;
  const pecsetek = pecsetekBol(msg.reviews).filter((p) => p.lang === nyelv);
  const sajat = msg.senderId === myUid;
  const jogosult = Boolean(es && es.forumTema === true && handlers && handlers.onReviewStart && String(szoveg || "").trim() !== "" && (!sajat || es.isStaff === true));
  const sajatErvenyes = pecsetek.some((p) => p.by === myUid && pecsetAllapot(msg, p) === "ervenyes");
  const naplo = pecsetNaploBol(msg.reviewsLog).filter((p) => p.lang === nyelv);
  // Más nyelvű szövegen lévő ellenőrzések / naplóbejegyzések (2026-10-18, a titkár jelzése: japán nézetben nem látszott a magyar szövegen tett pecsét):
  // a pecsét nyelvhez kötött, ezért itt csak jelezzük, hogy van - nyelvenként darabszámmal -, hogy ne tűnjön úgy, mintha nem lenne ellenőrzés.
  const masNyelvek = new Map();
  for (const p of pecsetekBol(msg.reviews)) if (p.lang !== nyelv) masNyelvek.set(p.lang, (masNyelvek.get(p.lang) || 0) + 1);
  for (const p of pecsetNaploBol(msg.reviewsLog)) if (p.lang !== nyelv && !masNyelvek.has(p.lang)) masNyelvek.set(p.lang, 0);
  if (pecsetek.length === 0 && !jogosult && naplo.length === 0 && masNyelvek.size === 0) return null;
  const doboz = el("div", { class: "msg-reviews" });
  for (const p of pecsetek) {
    const ervenyes = pecsetAllapot(msg, p) === "ervenyes";
    const nev = p.by === "deleted" ? t(uiLang, "msg.deletedSender") : p.byName || "?";
    const sor = el("div", { class: "msg-review" + (ervenyes ? "" : " stale"), title: p.note || null }, [
      el("span", { text: (ervenyes ? "✔ " : "⚠ ") + t(uiLang, ervenyes ? "message.reviewedBy" : "message.reviewStale", { name: nev, time: datum(p.at, uiLang) }) }),
    ]);
    if (p.note) sor.appendChild(el("span", { class: "msg-review-note", text: " — " + p.note }));
    if (handlers && handlers.onRemoveReview && (p.by === myUid || (es && es.isStaff === true))) {
      sor.appendChild(el("button", {
        type: "button", class: "text-btn msg-review-x", text: "✕", title: t(uiLang, "message.reviewRemove"), "aria-label": t(uiLang, "message.reviewRemove"),
        onclick: () => handlers.onRemoveReview(msg.messageId, nyelv, p.by === myUid ? null : p.by),
      }));
    }
    doboz.appendChild(sor);
  }
  if (masNyelvek.size > 0) {
    const lista = [...masNyelvek.entries()].map(([k, n]) => (n > 0 ? `${k.toUpperCase()} (${n})` : k.toUpperCase())).join(", ");
    doboz.appendChild(el("div", { class: "msg-review-other", text: "ℹ " + t(uiLang, "message.reviewOtherLangs", { langs: lista }) }));
  }
  // Korábbi (felváltott / visszavont) ellenőrzések: összecsukva, az ellenőrzött szöveggel és megjegyzéssel (bizonyíték, nem a mostani szövegre vonatkozik).
  if (naplo.length > 0) {
    const reszletek = el("details", { class: "msg-review-log" });
    reszletek.appendChild(el("summary", { text: "📜 " + t(uiLang, "message.reviewLogTitle", { n: naplo.length }) }));
    for (const p of [...naplo].reverse()) {
      const nev = p.by === "deleted" ? t(uiLang, "msg.deletedSender") : p.byName || "?";
      const vege = t(uiLang, p.ended === "replaced" ? "message.reviewEndedReplaced" : p.ended === "withdrawn" ? "message.reviewEndedWithdrawn" : "message.reviewEndedDropped");
      const sor = el("div", { class: "msg-review-log-row" }, [el("div", { class: "msg-review-log-head", text: `${nev} · ${datum(p.at, uiLang)} → ${vege}${p.endedAt ? " (" + datum(p.endedAt, uiLang) + ")" : ""}` })]);
      if (p.note) sor.appendChild(el("div", { class: "msg-review-note", text: p.note }));
      sor.appendChild(el("div", { class: "msg-review-log-text", text: p.text }));
      reszletek.appendChild(sor);
    }
    doboz.appendChild(reszletek);
  }
  // A pecsét megjegyzése (2026-10-18, a titkár kérése: "AI 081001 / a nyelv és a kör" a pecséten): a gomb előbb egy rövid, nem kötelező megjegyzés-mezőt nyit.
  const vazlat = es && es.postReviewing && es.postReviewing.messageId === msg.messageId && es.postReviewing.lang === nyelv ? es.postReviewing : null;
  if (jogosult && vazlat) {
    const szerk = el("div", { class: "msg-review-editor" });
    szerk.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "message.reviewNoteLabel") }));
    const mezo = el("input", { class: "panel-input", type: "text", maxlength: "300" });
    mezo.value = vazlat.note || "";
    mezo.addEventListener("input", () => handlers.onReviewNote(mezo.value));
    szerk.appendChild(mezo);
    if (vazlat.error) szerk.appendChild(el("div", { class: "panel-status err", text: vazlat.error }));
    const gombok = el("div", { class: "panel-row" });
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: vazlat.busy === true, onclick: handlers.onReviewCancel }));
    gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: vazlat.busy ? "…" : "✔ " + t(uiLang, "message.reviewAction"), disabled: vazlat.busy === true, onclick: handlers.onReviewConfirm }));
    szerk.appendChild(gombok);
    doboz.appendChild(szerk);
  } else if (jogosult && !sajatErvenyes) {
    doboz.appendChild(el("button", {
      type: "button", class: "msg-review-btn", text: "✔ " + t(uiLang, "message.reviewAction"), onclick: () => handlers.onReviewStart(msg.messageId, nyelv, String(szoveg)),
    }));
  }
  return doboz;
}

/**
 * Egy hozzászólás címkéi (kis címkék a buborék alatt) és - ha épp ezt jelölik - a címkeválasztó (2026-10-16).
 * A jelölés vázlata a modellben él (postTagging: { messageId, tags, busy, error }), a mentés a kezelőben.
 * @param {object} es { tagCatalog, postTagging }
 * @returns {HTMLElement|null}
 */
export function renderPostTags(msg, es, viewerLang, uiLang, handlers) {
  const katalogus = (es && es.tagCatalog) || [];
  const cimkek = temaCimkei(msg, katalogus);
  const vazlat = es && es.postTagging && es.postTagging.messageId === msg.messageId ? es.postTagging : null;
  if (cimkek.length === 0 && !vazlat) return null;
  const doboz = el("div", { class: "msg-tags" });
  if (cimkek.length > 0) {
    const sor = el("div", { class: "forum-row-meta" });
    // A címke kattintható (2026-10-16): a témán belüli szűrőt kapcsolja ("csak ezt a címkét viselő hozzászólások"), ha van kezelő.
    const szurt = (es && es.postSzuro) || [];
    for (const c of cimkek) {
      if (handlers && handlers.onPostFilterTag) {
        const be = szurt.includes(c.id);
        sor.appendChild(el("button", {
          type: "button", class: `tag-chip small tag-${c.group}` + (be ? " active" : ""), text: cimkeNev(c, viewerLang),
          title: t(uiLang, "forum.postFilter"), "aria-pressed": be ? "true" : "false", onclick: () => handlers.onPostFilterTag(c.id),
        }));
      } else {
        sor.appendChild(el("span", { class: `tag-chip small tag-${c.group}`, text: cimkeNev(c, viewerLang) }));
      }
    }
    doboz.appendChild(sor);
  }
  if (vazlat) {
    const valaszto = el("div", { class: "post-tag-picker" });
    if (katalogus.length === 0) valaszto.appendChild(el("p", { class: "hint", text: t(uiLang, "forum.tagsEmpty") }));
    for (const csoport of CIMKE_CSOPORTOK) {
      const tagek = katalogus.filter((c) => c.group === csoport);
      if (tagek.length === 0) continue;
      const sor = el("div", { class: "tag-row" }, [el("span", { class: "tag-row-label", text: t(uiLang, `forum.tagGroup_${csoport}`) })]);
      for (const c of tagek) {
        const jelolo = el("input", { type: "checkbox", value: c.id });
        jelolo.checked = vazlat.tags.includes(c.id);
        jelolo.addEventListener("change", () => handlers.onPostTagToggle(c.id, jelolo.checked));
        sor.appendChild(el("label", { class: "tag-check inline" }, [jelolo, el("span", { class: `tag-chip small tag-${c.group}`, text: cimkeNev(c, viewerLang) })]));
      }
      valaszto.appendChild(sor);
    }
    if (vazlat.error) valaszto.appendChild(el("div", { class: "panel-status err", text: vazlat.error }));
    const gombok = el("div", { class: "panel-row" });
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn compact", text: t(uiLang, "glossary.cancel"), disabled: vazlat.busy === true, onclick: handlers.onPostTagsCancel }));
    gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: vazlat.busy ? "…" : t(uiLang, "glossary.save"), disabled: vazlat.busy === true, onclick: handlers.onPostTagsSave }));
    valaszto.appendChild(gombok);
    doboz.appendChild(valaszto);
  }
  return doboz;
}

/**
 * A témán belüli hozzászólások címkeszűrője: csak a ténylegesen használt címkéket kínálja (model.postTagIds a katalógus
 * szerinti sorrendben, objektumként). Ha egyetlen hozzászólás sincs címkézve, nincs sor.
 * @param {object} model { uiLang, contentLang, postTags: katalógus-elemek, postSzuro: [azonosítók] }
 */
export function renderPostFilter(model, handlers) {
  const { uiLang, contentLang } = model;
  const tagek = model.postTags || [];
  if (tagek.length === 0) return null;
  const ki = model.postSzuro || [];
  const doboz = el("section", { class: "forum-filter post-filter", "aria-label": t(uiLang, "forum.postFilter") });
  const sor = el("div", { class: "tag-row" }, [el("span", { class: "tag-row-label", text: t(uiLang, "forum.postFilter") })]);
  for (const c of tagek) {
    const be = ki.includes(c.id);
    sor.appendChild(el("button", {
      type: "button", class: `tag-chip tag-${c.group}` + (be ? " active" : ""), text: cimkeNev(c, contentLang),
      "aria-pressed": be ? "true" : "false", onclick: () => handlers.onPostFilterTag(c.id),
    }));
  }
  if (ki.length > 0) sor.appendChild(el("button", { type: "button", class: "text-btn", text: t(uiLang, "forum.filterClear"), onclick: handlers.onPostFilterClear }));
  doboz.appendChild(sor);
  return doboz;
}

/**
 * @param {HTMLElement} box   a témalista helye (a chat-képernyő része)
 * @param {object} model      { uiLang, contentLang, currentRoomId, topics, forumRead, szobaIrhato, roomMuted,
 *                              emailFrequency, ujTema: null | {title, text}, ujTemaBusy, ujTemaHiba }
 * @param {object} handlers   onOpenTopic, onNewTopicStart, onNewTopicChange, onNewTopicCancel, onNewTopicSubmit,
 *                            onToggleRoomMute, onEmailFrequency
 */
export function renderTopicList(box, model, handlers) {
  const { uiLang, contentLang, currentRoomId } = model;
  box.innerHTML = "";

  // A címkekatalógus szerkesztője (admin) a lista helyett jelenik meg.
  if (model.katalogusDraft) {
    box.appendChild(renderCatalogEditor(model, handlers));
    return;
  }

  const utvonal = renderSzobaUtvonal(model, handlers);
  if (utvonal) box.appendChild(utvonal);

  // Útmutató-szoba (2026-10-19, a titkár #35): rövid leírás felül; új téma itt nem nyitható (a szerver is tiltja).
  const aktSzoba = (model.rooms || []).find((r) => r && r.roomId === currentRoomId);
  const utmutato = Boolean(aktSzoba && aktSzoba.guide === true);
  if (utmutato) {
    const leiras = utmutatoSzoveg(aktSzoba, uiLang);
    const kartya = el("section", { class: "guide-card", "aria-label": t(uiLang, "forum.guideTitle") });
    kartya.appendChild(el("h3", { class: "guide-title", text: t(uiLang, "forum.guideTitle") }));
    if (leiras) kartya.appendChild(el("p", { class: "guide-text", lang: uiLang, text: leiras }));
    // A valós alszobaszám (a titkár #46 kérése): "Válassz az alábbi 3 szoba közül" - ha a számuk változik, a szöveg követi; alszoba nélkül az általános figyelmeztetés.
    const alszobaDb = alszobakSzama(model, true);
    kartya.appendChild(el("p", { class: "hint guide-choose", text: alszobaDb > 0 ? t(uiLang, "forum.guideChoose", { n: alszobaDb }) : t(uiLang, "forum.guideNoNewTopic") }));
    box.appendChild(kartya);
  }

  // --- Eszközsor: új téma, szoba némítása, e-mail összefoglaló
  const sor = el("div", { class: "forum-toolbar" });
  if (model.szobaIrhato && !model.ujTema && !utmutato) {
    sor.appendChild(el("button", { type: "button", class: "primary-btn compact", text: t(uiLang, "forum.newTopic"), onclick: handlers.onNewTopicStart }));
  }
  sor.appendChild(el("button", {
    type: "button", class: "text-btn forum-mute-btn",
    text: t(uiLang, model.roomMuted ? "forum.unmuteRoom" : "forum.muteRoom"),
    title: t(uiLang, "forum.muteHint"), onclick: handlers.onToggleRoomMute,
  }));
  const email = el("select", { class: "panel-input forum-email", "aria-label": t(uiLang, "forum.emailLabel") });
  for (const f of ["off", "daily", "weekly"]) {
    const o = el("option", { value: f, text: t(uiLang, `forum.email_${f}`) });
    if ((model.emailFrequency || "off") === f) o.selected = true;
    email.appendChild(o);
  }
  email.addEventListener("change", () => handlers.onEmailFrequency(email.value));
  sor.appendChild(email);
  if (model.admin) {
    sor.appendChild(el("button", { type: "button", class: "text-btn", text: "🗂️ " + t(uiLang, "forum.catalogEdit"), onclick: handlers.onCatalogOpen }));
  }
  box.appendChild(sor);
  const szuroDoboz = szuroSor(model, handlers);
  if (szuroDoboz) box.appendChild(szuroDoboz);

  // --- Új téma űrlap (a gépelés nem rajzol újra: a kezelő csak megjegyzi)
  if (model.ujTema) {
    const urlap = el("section", { class: "forum-new-topic" });
    urlap.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "forum.titleLabel") }));
    const cim = el("input", { type: "text", class: "panel-input", maxlength: "150" });
    cim.value = model.ujTema.title || "";
    cim.addEventListener("input", () => handlers.onNewTopicChange("title", cim.value));
    urlap.appendChild(cim);
    urlap.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "forum.firstPostLabel") }));
    const szoveg = el("textarea", { class: "panel-input", rows: "5" });
    szoveg.value = model.ujTema.text || "";
    szoveg.addEventListener("input", () => handlers.onNewTopicChange("text", szoveg.value));
    urlap.appendChild(szoveg);
    if (model.ujTemaHiba) urlap.appendChild(el("div", { class: "panel-status err", text: model.ujTemaHiba }));
    const gombok = el("div", { class: "panel-row" });
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: model.ujTemaBusy, onclick: handlers.onNewTopicCancel }));
    gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: model.ujTemaBusy ? "…" : t(uiLang, "forum.publish"), disabled: model.ujTemaBusy, onclick: handlers.onNewTopicSubmit }));
    urlap.appendChild(gombok);
    box.appendChild(urlap);
  }

  // --- A témák
  if (!Array.isArray(model.topics)) {
    box.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
    return;
  }
  // Szülőszoba: előbb az alszobák (merre lehet továbbmenni), alattuk a szoba saját témái.
  const alszobak = renderSubRooms(model, handlers, false, utmutato);
  // Az ÜRES "Előzmények" sor (0 üzenet) nem téma, csak zaj: nem jelenik meg (v184, a Tomita-eset: a szülőszoba üresnek látszott, és a 0 üzenetes sor
  // elterelte a figyelmet az alszobák bejáratáról).
  const teljes = temaSorrend(model.topics).filter((x) => !(x.kind === "history" && !(Number(x.historyMessageCount) > 0)));
  // Szülőszobában, ha a szobának nincs saját témája, az alszobák a bejárat: nincs "A szoba témái" cím és "Még nincs téma" sor sem (v184).
  const csakAlszobak = Boolean(alszobak) && teljes.length === 0 && model.topicsMegerositetlen !== true;
  if (alszobak) {
    box.appendChild(alszobak);
    // Útmutató-szobában a régi témák összecsukhatók (lent): a bejárat az alszobákról szól, az előzmény ne nyomja el.
    if (!utmutato && !csakAlszobak) box.appendChild(el("h3", { class: "forum-section-title", text: t(uiLang, "forum.roomTopics") }));
  }
  const lista = el("div", { class: "forum-list" });
  const temak = szuresTemakre(teljes, model.forumSzuro, model.tagCatalog);
  if (teljes.length === 0 && model.topicsMegerositetlen === true) {
    // v181: üres, de a szerver által NEM igazolt (vagy hibás) lista: nem "még nincs téma", hanem jelzés + újrapróba.
    lista.appendChild(renderListaJelzes(uiLang, handlers.onRetryList));
  } else if (csakAlszobak) {
    /* a bejárat az alszobák; nincs mit mondani a (nem létező) saját témákról */
  } else if (temak.length === 0 && teljes.length === 0 && !alszobak && !utmutato) {
    // Üres szoba útmutatása (v170): mi ez a hely, és mit lehet itt tenni (rövid, tegező hang).
    const ures = el("section", { class: "empty-room-guide", "aria-label": t(uiLang, "forum.noTopics") });
    ures.appendChild(el("p", { class: "empty-room-title", text: t(uiLang, "forum.noTopics") }));
    ures.appendChild(el("p", { class: "hint empty-room-text", text: t(uiLang, model.szobaIrhato ? "forum.emptyGuideWrite" : "forum.emptyGuideRead") }));
    lista.appendChild(ures);
  } else if (temak.length === 0) lista.appendChild(el("p", { class: "hint", text: t(uiLang, teljes.length === 0 ? "forum.noTopics" : "forum.filterNoMatch") }));
  for (const tema of temak) {
    const elozmeny = tema.kind === "history";
    // Átirányító (2026-10-15): a téma önálló szobává alakult - a sor az új szobát nyitja.
    if (tema.kind === "moved") {
      lista.appendChild(el("button", { type: "button", class: "forum-row forum-moved", onclick: () => handlers.onOpenMovedTopic(tema) }, [
        el("span", { class: "forum-row-title", text: "➡️ " + temaCim(tema, contentLang) }),
        el("span", { class: "forum-row-sub", text: t(uiLang, tema.movedToTopicId ? "forum.movedTopicSub" : "forum.movedSub") }),
      ]));
      continue;
    }
    const uj = olvasatlanDb(tema, currentRoomId, model.forumRead);
    // Az "Előzmények" sor CÍM: mint a többi téma címe, a CSEVEGÉS nyelvén áll (nem a felületén) - 2026-10-16, a képernyőkép: olasz felület,
    // francia csevegés: a címek franciák voltak, csak ez az egy olasz. A sor alatti számok / dátumok felületi szöveg, azok a felület nyelvén maradnak.
    const cim = elozmeny ? t(contentLang, "forum.historyTitle") : temaCim(tema, contentLang);
    const jelek = [tema.pinned && !elozmeny ? "📌" : "", tema.status === "closed" && !elozmeny ? "🔒" : "", tema.locked && !elozmeny ? "⛔" : ""].filter(Boolean).join(" ");
    const sub = elozmeny
      ? t(uiLang, "forum.historySub", { n: tema.historyMessageCount, from: datum(tema.historyFrom, uiLang, false), to: datum(tema.historyTo, uiLang, false) })
      : t(uiLang, "forum.topicSub", { n: tema.postCount, who: utolsoHozzaszolo(tema, contentLang, t(uiLang, "msg.deletedSender"), pickSenderName) || "?", time: datum(tema.lastPostAt, uiLang) });
    // Státusz-jelvény és címkék (2026-10-16): egy pillantásra látszik, hol tart a téma anyaga.
    const cimkek = temaCimkei(tema, model.tagCatalog);
    const meta = !elozmeny && (tema.workflow || cimkek.length > 0 || tema.workflowAssignee || tema.workflowDue)
      ? el("span", { class: "forum-row-meta" }, [
        tema.workflow ? allapotJelveny(uiLang, tema.workflow) : null,
        felelosHataridoJelzes(tema, uiLang),
        ...cimkek.map((c) => el("span", { class: `tag-chip small tag-${c.group}`, text: cimkeNev(c, contentLang) })),
      ])
      : null;
    const sorElem = el("button", {
      type: "button",
      class: "forum-row" + (elozmeny ? " forum-history" : "") + (uj > 0 ? " forum-unread" : ""),
      onclick: () => handlers.onOpenTopic(tema.topicId),
    }, [
      el("span", { class: "forum-row-title", text: (jelek ? jelek + " " : "") + cim }),
      meta,
      el("span", { class: "forum-row-sub", text: sub }),
      uj > 0 ? el("span", { class: "forum-badge", text: String(uj), "aria-label": t(uiLang, "forum.unreadCount", { n: uj }) }) : null,
    ]);
    lista.appendChild(sorElem);
  }
  if (utmutato && alszobak) {
    // Összecsukható "Korábbi beszélgetések megtekintése (N)": alapból zárt; a nyitott állapotot a frissítések (élő lista) is megtartják.
    const regiDb = temak.filter((x) => x.kind !== "history").length;
    if (regiDb > 0) {
      const reszletek = el("details", { class: "guide-old" });
      reszletek.open = guideRegiNyitva;
      reszletek.addEventListener("toggle", () => { guideRegiNyitva = reszletek.open; });
      reszletek.appendChild(el("summary", { class: "guide-old-summary", text: t(uiLang, "forum.guideOldSummary", { n: regiDb }) }));
      reszletek.appendChild(lista);
      box.appendChild(reszletek);
    }
  } else box.appendChild(lista);
}

/**
 * A megnyitott téma sávja a fejléc alatt: vissza a témákhoz, a cím, figyelés be/ki, állapot.
 * @param {object} model { uiLang, contentLang, aktTema, watching, temaIrhato }
 */
export function renderTopicBar(model, handlers) {
  const { uiLang, contentLang, aktTema } = model;
  const bar = el("div", { class: "forum-topic-bar" });
  // A kezelő ikonok (státusz, címkék, átnevezés, kitűzés, lezárás...) külön sorba, a "Kezelés" gombbal nyíló menübe kerülnek:
  // keskeny képernyőn a "Vissza" mellett sorakozó kis ikonok miatt a tag nem találta a visszaút gombját (2026-10-18, tagi jelzés).
  const sorMenu = el("div", { class: "forum-manage-row" });
  const hovaVissza = (model.rooms || []).find((r) => r && r.roomId === model.currentRoomId);
  // Feliratos, nagy érintési felületű "Vissza" (2026-10-19, a titkár #35): a felirat megmondja, hová vezet.
  bar.appendChild(el("button", {
    type: "button", class: "text-btn forum-back",
    text: "← " + (hovaVissza ? t(uiLang, "forum.backTo", { room: getRoomDisplayName(hovaVissza, contentLang, uiLang) }) : t(uiLang, "forum.backToTopics")),
    onclick: handlers.onBackToTopics,
  }));
  const elozmeny = aktTema && aktTema.kind === "history";
  bar.appendChild(el("span", { class: "forum-topic-title", text: elozmeny ? t(contentLang, "forum.historyTitle") : temaCim(aktTema, contentLang) }));
  // Kezelés (2026-10-09): a téma nyitójának és az admin / témafelelősnek. Az átnevezés és a "cím
  // újrafordítása" a nyitóé is; a kitűzés és a lezárás csak az admin / témafelelősé (a szerver is így dönt).
  // Aki nem kezelheti a témát, annak is látszik a státusz (csak olvasható jelvényként).
  // Aki nem kezelheti a témát, annak a jelvény gomb: az előzményt nyitja (ki, mikor, miért váltott).
  if (aktTema && !elozmeny && !model.temaKezelhet && aktTema.workflow) {
    const jelveny = allapotJelveny(uiLang, aktTema.workflow);
    if (handlers && handlers.onOpenWorkflowPanel) {
      const gomb = el("button", { type: "button", class: "text-btn wf-badge-btn", title: t(uiLang, "forum.workflowHistory"), "aria-label": t(uiLang, "forum.workflowHistory"), onclick: handlers.onOpenWorkflowPanel }, [jelveny]);
      bar.appendChild(gomb);
    } else bar.appendChild(jelveny);
  }
  if (aktTema && !elozmeny) {
    const gomb = (ikon, kulcs, kezelo) => sorMenu.appendChild(el("button", {
      type: "button", class: "text-btn forum-manage-btn", text: ikon, title: t(uiLang, kulcs), "aria-label": t(uiLang, kulcs), onclick: kezelo,
    }));
    if (model.temaKezelhet) {
      const sel = el("select", { class: "panel-input wf-select", "aria-label": t(uiLang, "forum.workflowLabel"), title: t(uiLang, "forum.workflowLabel") });
      sel.appendChild(el("option", { value: "", text: t(uiLang, "forum.wf_none") }));
      for (const a of WORKFLOW_ALLAPOTOK) {
        const o = el("option", { value: a, text: t(uiLang, `forum.wf_${a}`) });
        if (aktTema.workflow === a) o.selected = true;
        sel.appendChild(o);
      }
      sel.addEventListener("change", () => handlers.onSetWorkflow(sel.value));
      sorMenu.appendChild(sel);
      gomb("🔖", "forum.tagsEdit", handlers.onEditTags);
      gomb("📚", "forum.sourceEdit", handlers.onEditSource);
      gomb("🕘", "forum.workflowHistory", handlers.onOpenWorkflowPanel);
      gomb("✏️", "forum.renameTopic", handlers.onRenameTopic);
      gomb("🌐", "forum.retranslateTitle", handlers.onRetranslateTitle);
    }
    // Címek nyelvenként (fix, javított) - csak admin; a szerver is csak adminnak engedi.
    if (model.admin) gomb("🏷️", "forum.titlesEdit", handlers.onEditTitles);
    if (model.staff) {
      gomb(aktTema.pinned ? "📍" : "📌", aktTema.pinned ? "forum.unpinTopic" : "forum.pinTopic", handlers.onTogglePin);
      gomb(aktTema.status === "closed" ? "🔓" : "🔒", aktTema.status === "closed" ? "forum.reopenTopic" : "forum.closeTopic", handlers.onToggleClose);
    }
    // Téma átalakítása önálló főszobává (2026-10-15) - csak admin; a szerver is csak adminnak engedi.
    if (model.admin && aktTema.kind === "discussion") gomb("🏠", "forum.convertToRoom", handlers.onConvertToRoom);
  }
  // A "Kezelés" gomb csak annak jelenik meg, akinek van mit kezelnie; a menü nyitva-zárva állapota a modellben él (temaMenu).
  const menuNyitva = model.temaMenu === true;
  if (sorMenu.children.length > 0) {
    bar.appendChild(el("button", {
      type: "button", class: "text-btn forum-menu-btn" + (menuNyitva ? " active" : ""), text: "⋯ " + t(uiLang, "forum.manageMenu"),
      "aria-expanded": menuNyitva ? "true" : "false", onclick: handlers.onToggleTopicMenu,
    }));
  }
  if (aktTema && !elozmeny) {
    bar.appendChild(el("button", {
      type: "button", class: "text-btn forum-watch-btn" + (model.watching ? " active" : ""),
      text: model.watching ? "🔔" : "🔕",
      title: t(uiLang, model.watching ? "forum.watchingHint" : "forum.notWatchingHint"),
      "aria-label": t(uiLang, model.watching ? "forum.watchingHint" : "forum.notWatchingHint"),
      onclick: handlers.onToggleWatch,
    }));
  }
  const doboz = el("div", { class: "forum-topic-head" }, [bar]);
  if (menuNyitva && sorMenu.children.length > 0) doboz.appendChild(sorMenu);
  return doboz;
}

/**
 * A "címek nyelvenként" szerkesztő (admin, 2026-10-09): minden rendszernyelvhez egy mező. Amit az admin kitölt,
 * azt a rendszer FIXEN használja, és azt a nyelvet a gép nem fordítja; az üres mezőnél a gépi fordítás marad
 * (a mező halvány súgója). A nyelvek listája a nyelvek.json-ból jön, új nyelv felvételekor automatikusan bővül.
 * @param {object} model { uiLang, aktTema, nyelvNevek, titlesDraft, titlesBusy, titlesError, titlesStatus }
 */
/**
 * A téma ÁTNEVEZÉSE (2026-10-20, v122): beépített szerkesztő a böngésző felugró kérdése (window.prompt) helyett - az iPhone-on / telepített
 * alkalmazásban / beágyazott böngészőben néha letiltott vagy nem látszik, és a címfrissítés nem sikerült (Mamoru jelezte: "nem tudom módosítani a címet").
 * A cím a kijelölt nyelven (a saját csevegési nyelven) áll, a többi nyelvre újrafordul.
 * @param {object} model { uiLang, atnevDraft: {title, busy, error} }
 * @param {object} handlers onRenameChange(ertek), onRenameCancel, onRenameSave
 */
export function renderAtnevezes(model, handlers) {
  const { uiLang } = model;
  const v = model.atnevDraft || {};
  const box = el("section", { class: "panel-section forum-rename-editor" });
  box.appendChild(el("h3", { text: t(uiLang, "forum.renameTopic") }));
  const mezo = el("input", { type: "text", class: "panel-input", maxlength: "150", "aria-label": t(uiLang, "forum.renamePrompt") });
  mezo.value = v.title || "";
  mezo.addEventListener("input", () => handlers.onRenameChange(mezo.value));
  mezo.addEventListener("keydown", (e) => {
    if (e.isComposing || e.keyCode === 229) return; // japán bevitel: az Enter a jelölt megerősítése
    if (e.key === "Enter") handlers.onRenameSave();
    if (e.key === "Escape") handlers.onRenameCancel();
  });
  box.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "forum.renamePrompt") }));
  box.appendChild(mezo);
  if (v.error) box.appendChild(el("div", { class: "panel-status err", role: "alert", text: v.error }));
  box.appendChild(el("div", { class: "panel-row" }, [
    el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: v.busy === true, onclick: handlers.onRenameCancel }),
    el("button", { type: "button", class: "primary-btn compact", text: v.busy ? "…" : t(uiLang, "glossary.save"), disabled: v.busy === true, onclick: handlers.onRenameSave }),
  ]));
  return box;
}

export function renderTitlesEditor(model, handlers) {
  const { uiLang, aktTema } = model;
  const box = el("section", { class: "panel-section forum-titles-editor" });
  box.appendChild(el("h3", { text: t(uiLang, "forum.titlesEdit") }));
  box.appendChild(el("p", { class: "hint", text: t(uiLang, "forum.titlesHint") }));
  const draft = model.titlesDraft || {};
  for (const sor of cimSzerkesztoSorok(aktTema, model.nyelvNevek)) {
    box.appendChild(el("label", { class: "panel-label small", text: `${sor.nev} (${sor.kod})${sor.forras ? " ★" : ""}` }));
    const mezo = el("input", { type: "text", class: "panel-input", maxlength: "150", placeholder: sor.sugo || "" });
    mezo.value = draft[sor.kod] !== undefined ? draft[sor.kod] : sor.ertek;
    mezo.addEventListener("input", () => handlers.onTitlesChange(sor.kod, mezo.value));
    box.appendChild(mezo);
  }
  if (model.titlesError) box.appendChild(el("div", { class: "panel-status err", text: model.titlesError }));
  if (model.titlesStatus) box.appendChild(el("div", { class: "panel-status ok", text: model.titlesStatus }));
  const gombok = el("div", { class: "panel-row" });
  gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: model.titlesBusy, onclick: handlers.onTitlesCancel }));
  gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: model.titlesBusy ? "…" : t(uiLang, "glossary.save"), disabled: model.titlesBusy, onclick: handlers.onTitlesSave }));
  box.appendChild(gombok);
  return box;
}
